import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Popover,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  TableSortLabel,
  Badge,
  Divider
} from '@mui/material';

import {
  Search,
  FilterList,
  Add,
  Edit,
  Delete,
  Visibility,
  Warning,
  CheckCircle,
  Pending,
  Cancel,
  Download,
  Refresh,
  Category,
  LocalShipping,
  Clear,
  Inventory2,
  Store,
  Assignment,
  Check,
  Close,
  ShoppingCart,
  Verified,
  MoreVert,
  TrendingUp,
  TrendingDown,
  CalendarToday,
  AddCircle,
  ListAlt
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import Sidebar from '../../../../components/sidebars/SideBarStoreKeep';
import TopNav from '../../../../components/TopNav_hotelStaff';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  maxWidth: '100%',
  '& .MuiTable-root': {
    minWidth: 800,
    width: '100%',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backdropFilter: 'blur(8px)',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    transition: 'background-color 0.2s ease',
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  borderRadius: '20px',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '28px',
  ...(status === 'In Stock' && {
    backgroundColor: alpha(theme.palette.success.main, 0.12),
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  }),
  ...(status === 'Low Stock' && {
    backgroundColor: alpha(theme.palette.warning.main, 0.12),
    color: theme.palette.warning.main,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
  }),
  ...(status === 'Out of Stock' && {
    backgroundColor: alpha(theme.palette.error.main, 0.12),
    color: theme.palette.error.main,
    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
  }),
  ...(status === 'Approved' && {
    backgroundColor: alpha(theme.palette.success.main, 0.12),
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  }),
  ...(status === 'Pending' && {
    backgroundColor: alpha(theme.palette.warning.main, 0.12),
    color: theme.palette.warning.main,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
  }),
  ...(status === 'Rejected' && {
    backgroundColor: alpha(theme.palette.error.main, 0.12),
    color: theme.palette.error.main,
    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
  }),
}));

const GlowingButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

// Helper function to get current user details from localStorage
const getCurrentUserDetails = () => {
  try {
    // Try to get user profile from localStorage (set during login)
    const userProfileStr = localStorage.getItem('userProfile');
    if (userProfileStr) {
      const userProfile = JSON.parse(userProfileStr);
      return {
        staff_id: userProfile.staff_id || userProfile.id || 'Unknown',
        full_name: userProfile.full_name || 
                   `${userProfile.first_name || ''} ${userProfile.other_names || ''}`.trim() || 
                   userProfile.email || 
                   'Staff Member',
        first_name: userProfile.first_name,
        other_names: userProfile.other_names,
        email: userProfile.email,
        department: userProfile.department,
        user_type: userProfile.user_type,
        hotel_code: userProfile.hotel_code,
        phone: userProfile.phone
      };
    }
    
    // Fallback: try to get from individual localStorage items
    const staffId = localStorage.getItem('staffId') || localStorage.getItem('staff_id');
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    const userRole = localStorage.getItem('role');
    const userDepartment = localStorage.getItem('department');
    
    return {
      staff_id: staffId || 'Unknown',
      full_name: userName || userEmail || 'Unknown User',
      email: userEmail,
      department: userDepartment,
      user_type: userRole
    };
  } catch (error) {
    console.error('Error getting user details:', error);
    return {
      staff_id: 'Unknown',
      full_name: 'Unknown User',
      email: '',
      department: '',
      user_type: ''
    };
  }
};

const InventoryManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [loggedInUser, setLoggedInUser] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  
  // Inventory states (from API)
  const [inventoryItems, setInventoryItems] = useState([]);
  const [totalInventory, setTotalInventory] = useState(0);
  
  // Supplies states (API integrated)
  const [supplies, setSupplies] = useState([]);
  const [totalSupplies, setTotalSupplies] = useState(0);
  const [totalApprovedSupplies, setTotalApprovedSupplies] = useState(0);
  const [totalPendingSupplies, setTotalPendingSupplies] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  
  
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  
  // Form data for supplies
  const [formData, setFormData] = useState({
    item_name: '',
    category: '',
    unit_of_measurement: '',
    quantity_received: '',
    total_invoice_value: '',
    checked_by: '',
    expiry_date: ''
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Unit options
  const unitOptions = ['pcs', 'kg', 'litres', 'bunches', 'packs', 'boxes', 'reams', 'units'];

  // Sorting function
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Fetch inventory items from inventory_main table
  const fetchInventoryItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_inventory_items.php?${params}`);
      if (response.data.success) {
        setInventoryItems(response.data.data);
        setTotalInventory(response.data.total);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch inventory items',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, selectedStatus, page, rowsPerPage]);

  // Fetch supplies from backend
  const fetchSupplies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_supplies.php?${params}`);
      if (response.data.success) {
        setSupplies(response.data.data);
        setTotalSupplies(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching supplies:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, dateRange, page, rowsPerPage]);

  // Fetch supply statistics (approved and pending counts)
  const fetchSupplyStats = useCallback(async () => {
    try {
      // Fetch approved supplies count
      const approvedResponse = await axios.get(`${API_BASE_URL}/hotel/inventory/get_approved_supplies_count.php`);
      if (approvedResponse.data.success) {
        setTotalApprovedSupplies(approvedResponse.data.count);
      }
      
      // Fetch pending supplies count
      const pendingResponse = await axios.get(`${API_BASE_URL}/hotel/inventory/get_pending_supplies_count.php`);
      if (pendingResponse.data.success) {
        setTotalPendingSupplies(pendingResponse.data.count);
      }
    } catch (error) {
      console.error('Error fetching supply stats:', error);
    }
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_categories.php`);
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Fetch items list for dropdown
  const fetchItemsList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_item_names.php`);
      if (response.data.success) {
        setItemsList(response.data.items);
      }
    } catch (error) {
      console.error('Error fetching items list:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      fetchInventoryItems();
      fetchCategories();
    } else if (activeTab === 1) {
      fetchSupplies();
      fetchSupplyStats();
      fetchCategories();
      fetchItemsList();
    }
  }, [activeTab, fetchInventoryItems, fetchSupplies, fetchSupplyStats, fetchCategories, fetchItemsList]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedCategory, selectedStatus, dateRange]);

  // Handle item selection from dropdown
  const handleItemSelect = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        item_name: newValue.item_name,
        category: newValue.category,
        unit_of_measurement: newValue.unit_of_measurement || ''
      });
    } else {
      setFormData({
        ...formData,
        item_name: '',
        category: '',
        unit_of_measurement: ''
      });
    }
  };

  // Get logged-in user details from localStorage
  useEffect(() => {
    const userDetails = getCurrentUserDetails();
    setCurrentUser(userDetails);
    setLoggedInUser(userDetails.full_name);
  }, []);

  // Add new supply - UPDATED with proper user details
  const handleAddSupply = async () => {
    if (!formData.item_name || !formData.category || !formData.unit_of_measurement || 
        !formData.quantity_received || !formData.total_invoice_value) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      // Get current user details
      const userDetails = getCurrentUserDetails();
      const addedBy = userDetails.full_name;
      const staffId = userDetails.staff_id;
      const userDepartment = userDetails.department;
      
      // Create a detailed added_by string
      const addedByString = `${addedBy} (ID: ${staffId})${userDepartment ? ` - ${userDepartment}` : ''}`;
      
      const payload = {
        item_name: formData.item_name,
        category: formData.category,
        unit_of_measurement: formData.unit_of_measurement,
        quantity_received: parseFloat(formData.quantity_received),
        total_invoice_value: parseFloat(formData.total_invoice_value),
        added_by_staffid: addedByString,
        checked_by: formData.checked_by || addedBy,
        expiry_date: formData.expiry_date || null,
        department: userDepartment,
        staff_id: staffId
      };

      console.log('Adding supply with user:', { addedBy, staffId, department: userDepartment });

      const response = await axios.post(`${API_BASE_URL}/hotel/inventory/add_supply.php`, payload);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Supply added successfully',
          severity: 'success'
        });
        handleCloseAddDialog();
        fetchSupplies();
        fetchSupplyStats();
        fetchItemsList();
        
        // Reset form
        setFormData({
          item_name: '',
          category: '',
          unit_of_measurement: '',
          quantity_received: '',
          total_invoice_value: '',
          checked_by: '',
          expiry_date: ''
        });
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error adding supply:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to add supply',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Edit supply - UPDATED with user tracking
  const handleEditSupply = async () => {
    setLoading(true);
    try {
      // Get current user for audit trail
      const userDetails = getCurrentUserDetails();
      const updatedBy = userDetails.full_name;
      
      const payload = {
        id: selectedSupply.id,
        item_name: formData.item_name,
        category: formData.category,
        unit_of_measurement: formData.unit_of_measurement,
        quantity_received: parseFloat(formData.quantity_received),
        total_invoice_value: parseFloat(formData.total_invoice_value),
        checked_by: formData.checked_by || updatedBy,
        expiry_date: formData.expiry_date || null,
        updated_by: updatedBy,
        updated_by_staff_id: userDetails.staff_id,
        updated_at: new Date().toISOString()
      };

      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/hotel/inventory/update_supply.php`,
        data: payload,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Supply updated successfully',
          severity: 'success'
        });
        handleCloseEditDialog();
        fetchSupplies();
        fetchSupplyStats();
        fetchItemsList();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error updating supply:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to update supply',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete supply - UPDATED with user tracking
  const handleDeleteSupply = async () => {
    setLoading(true);
    try {
      // Get current user for audit trail
      const userDetails = getCurrentUserDetails();
      
      const response = await axios({
        method: 'delete',
        url: `${API_BASE_URL}/hotel/inventory/delete_supply.php`,
        data: { 
          id: selectedSupply.id,
          deleted_by: userDetails.full_name,
          deleted_by_staff_id: userDetails.staff_id,
          deletion_reason: 'Deleted by store keeper'
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Supply deleted successfully',
          severity: 'success'
        });
        handleCloseDeleteDialog();
        fetchSupplies();
        fetchSupplyStats();
        fetchItemsList();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error deleting supply:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to delete supply',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const handleExportToExcel = () => {
    let exportData = [];
    let fileName = '';
    
    if (activeTab === 0) {
      exportData = inventoryItems.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Quantity Received': item.quantity_received,
        'Quantity Issued': item.quantity_issued,
        'Quantity Available': item.quantity_available,
        'Quantity Damaged': item.quantity_damaged,
        'Storage Location': item.storage_location || 'N/A',
        'Date Received': item.date_received,
        'Added By': item.added_by_staffid,
        'Notes': item.notes || 'N/A'
      }));
      fileName = 'inventory_items';
    } else if (activeTab === 1) {
      exportData = supplies.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Quantity Received': item.quantity_received,
        'Total Invoice Value (₦)': item.total_invoice_value,
        'Date Received': item.date_received,
        'Approval Status': item.approval_status === 1 ? 'Approved' : 'Pending',
        'Added By': item.added_by_staffid,
        'Expiry Date': item.expiry_date || 'N/A'
      }));
      fileName = 'supplies';
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setSnackbar({
      open: true,
      message: 'Export successful!',
      severity: 'success'
    });
  };

  const handleOpenAddDialog = () => {
    // Pre-fill checked_by with current user name
    const userDetails = getCurrentUserDetails();
    setFormData({
      item_name: '',
      category: '',
      unit_of_measurement: '',
      quantity_received: '',
      total_invoice_value: '',
      checked_by: userDetails.full_name,
      expiry_date: ''
    });
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = (supply) => {
    if (supply.approval_status === 1) {
      setSnackbar({
        open: true,
        message: 'Cannot edit approved supplies. Please request approval for changes.',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedSupply(supply);
    setFormData({
      item_name: supply.item_name,
      category: supply.category,
      unit_of_measurement: supply.unit_of_measurement,
      quantity_received: supply.quantity_received,
      total_invoice_value: supply.total_invoice_value,
      checked_by: supply.checked_by || getCurrentUserDetails().full_name,
      expiry_date: supply.expiry_date || ''
    });
    setEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (supply) => {
    if (supply.approval_status === 1) {
      setSnackbar({
        open: true,
        message: 'Cannot delete approved supplies. Please request deletion approval.',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedSupply(supply);
    setDeleteDialogOpen(true);
  };

  const handleCloseAddDialog = () => setAddDialogOpen(false);
  const handleCloseEditDialog = () => setEditDialogOpen(false);
  const handleCloseDeleteDialog = () => setDeleteDialogOpen(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setDateRange({ start: '', end: '' });
    setOrderBy('');
    setOrder('asc');
  };

  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setDateRange({ start: '', end: '' });
    setPage(0);
    handleFilterClose();
    if (activeTab === 0) {
      fetchInventoryItems();
    } else if (activeTab === 1) {
      fetchSupplies();
      fetchSupplyStats();
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'In Stock': return <CheckCircle fontSize="small" />;
      case 'Low Stock': return <Warning fontSize="small" />;
      case 'Out of Stock': return <Cancel fontSize="small" />;
      case 'Approved': return <CheckCircle fontSize="small" />;
      case 'Pending': return <Pending fontSize="small" />;
      case 'Rejected': return <Cancel fontSize="small" />;
      default: return null;
    }
  };

  const getStockStatus = (quantityAvailable, lowstockThreshold = 5) => {
    if (quantityAvailable <= 0) return 'Out of Stock';
    if (quantityAvailable <= lowstockThreshold) return 'Low Stock';
    return 'In Stock';
  };

  // Sort data
  const sortData = (data) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_received' || orderBy === 'quantity_available' || 
          orderBy === 'quantity_issued' || orderBy === 'total_invoice_value') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (orderBy === 'date_received' || orderBy === 'created_at' || orderBy === 'lastUpdated' || orderBy === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Get unique categories from inventory items
  const getInventoryCategories = () => {
    const uniqueCategories = [...new Set(inventoryItems.map(item => item.category).filter(Boolean))];
    return ['all', ...uniqueCategories];
  };

  // Render Inventory Table from inventory_main
  const renderInventoryTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Received
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_issued'}
                direction={orderBy === 'quantity_issued' ? order : 'asc'}
                onClick={() => handleSort('quantity_issued')}
              >
                Issued
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_available'}
                direction={orderBy === 'quantity_available' ? order : 'asc'}
                onClick={() => handleSort('quantity_available')}
              >
                Available
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Status</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>Storage Location</TableCell>
            <TableCell align="center" sx={{ minWidth: 100 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : inventoryItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No inventory items found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortData(inventoryItems).map((item) => {
              const stockStatus = getStockStatus(item.quantity_available, item.lowstock_threshold);
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                        <Inventory2 sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.category || 'Uncategorized'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {Number(item.quantity_received).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {Number(item.quantity_issued).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={stockStatus === 'Low Stock' ? 700 : 500}>
                      {Number(item.quantity_available).toLocaleString()}
                    </Typography>
                    {stockStatus === 'Low Stock' && item.lowstock_threshold && (
                      <LinearProgress 
                        variant="determinate" 
                        value={(item.quantity_available / (item.lowstock_threshold * 2)) * 100} 
                        sx={{ mt: 0.5, height: 3, borderRadius: 2 }}
                        color="warning"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip 
                      icon={getStatusIcon(stockStatus)}
                      label={stockStatus} 
                      status={stockStatus}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption">{item.date_received}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.storage_location || 'Not specified'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => {
                        setSnackbar({
                          open: true,
                          message: `View details for ${item.item_name} - Coming soon`,
                          severity: 'info'
                        });
                      }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                   
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 25, 50, 100]}
        component="div"
        count={totalInventory}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Supplies Table
  const renderSuppliesTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Received Qty
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={orderBy === 'total_invoice_value'}
                direction={orderBy === 'total_invoice_value' ? order : 'asc'}
                onClick={() => handleSort('total_invoice_value')}
              >
                Total Invoice (₦)
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Approval Status</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Added By</TableCell>
            <TableCell sx={{ minWidth: 120 }}>Expiry Date</TableCell>
            <TableCell align="center" sx={{ minWidth: 120 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : supplies.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No supplies found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortData(supplies).map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Store sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.category} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{item.unit_of_measurement}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_received).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    ₦{Number(item.total_invoice_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.date_received}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    icon={item.approval_status === 1 ? <Verified sx={{ fontSize: 14 }} /> : <Pending sx={{ fontSize: 14 }} />}
                    label={item.approval_status === 1 ? 'Approved' : 'Pending'}
                    color={item.approval_status === 1 ? 'success' : 'warning'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={`Added by: ${item.added_by_staffid || 'Unknown'}`}>
                    <Chip 
                      label={item.added_by_staffid || 'Unknown'}
                      size="small"
                      variant="outlined"
                      sx={{ maxWidth: '150px' }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {item.expiry_date && (
                    <Typography 
                      variant="caption" 
                      color={new Date(item.expiry_date) < new Date() ? 'error.main' : 'text.secondary'}
                    >
                      {item.expiry_date}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={item.approval_status === 1 ? "Cannot edit approved supply" : "Edit"}>
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenEditDialog(item)}
                        disabled={item.approval_status === 1}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={item.approval_status === 1 ? "Cannot delete approved supply" : "Delete"}>
                    <span>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleOpenDeleteDialog(item)}
                        disabled={item.approval_status === 1}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 25, 50, 100]}
        component="div"
        count={totalSupplies}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Requisitions Table
  const renderCurrentTable = () => {
    if (activeTab === 0) return renderInventoryTable();
    if (activeTab === 1) return renderSuppliesTable();
    return null;
  };

  const getAddButtonText = () => {
    if (activeTab === 0) return 'Add Inventory Item';
    if (activeTab === 1) return 'Add Supply';
    if (activeTab === 2) return 'Add Requisition';
    return 'Add';
  };

  const handleAddClick = () => {
    if (activeTab === 1) {
      handleOpenAddDialog();
    } else {
      setSnackbar({
        open: true,
        message: `${getAddButtonText()} feature coming soon`,
        severity: 'info'
      });
    }
  };

  // Statistics - using total counts from API
  const statistics = useMemo(() => {
    if (activeTab === 0) {
      const lowStock = inventoryItems.filter(item => {
        const stockStatus = getStockStatus(item.quantity_available, item.lowstock_threshold);
        return stockStatus === 'Low Stock';
      }).length;
      const outOfStock = inventoryItems.filter(item => {
        const stockStatus = getStockStatus(item.quantity_available, item.lowstock_threshold);
        return stockStatus === 'Out of Stock';
      }).length;
      return { totalItems: totalInventory, lowStock, outOfStock };
    } else if (activeTab === 1) {
      // Use the total counts from separate API calls
      return { 
        totalItems: totalSupplies, 
        approvedSupplies: totalApprovedSupplies, 
        pendingSupplies: totalPendingSupplies 
      };
    }
    return { totalItems: 0 };
  }, [activeTab, inventoryItems, totalInventory, totalSupplies, totalApprovedSupplies, totalPendingSupplies]);
  
  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh', overflowX: 'hidden' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        transition: 'margin 0.3s ease-in-out',
        maxWidth: '1600px',
        mx: 'auto',
        width: '100%',
        overflowX: 'hidden'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 3, width: '100%', overflowX: 'hidden' }}>
          <Paper elevation={0} sx={{ borderRadius: '20px', mb: 3, overflow: 'hidden', width: '100%' }}>
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              sx={{
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  py: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  }
                },
                '& .Mui-selected': {
                  color: theme.palette.primary.main,
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                }
              }}
            >
              <Tab icon={<Inventory2 />} iconPosition="start" label={`Inventory (${statistics.totalItems})`} />
              <Tab icon={<Store />} iconPosition="start" label={`Supplies (${statistics.totalItems})`} />
            </Tabs>

            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ p: 3, backgroundColor: alpha(theme.palette.primary.main, 0.01) }}>
              <Grid item xs={12} sm={activeTab === 2 ? 6 : activeTab === 1 ? 4 : 4}>
                <StyledCard>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          Total {activeTab === 0 ? 'Items' : activeTab === 1 ? 'Supplies' : 'Requisitions'}
                        </Typography>
                        <Typography variant="h4" fontWeight={700} mt={1}>
                          {statistics.totalItems}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                        {activeTab === 0 ? <Inventory2 /> : activeTab === 1 ? <Store /> : <Assignment />}
                      </Avatar>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              {activeTab === 1 && (
                <>
                  <Grid item xs={12} sm={4}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Approved Supplies
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="success.main">
                              {statistics.approvedSupplies}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                            <Verified />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Pending Supplies
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="warning.main">
                              {statistics.pendingSupplies}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                            <Pending />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                </>
              )}
              
              {activeTab === 0 && (
                <>
                  <Grid item xs={12} sm={4}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Low Stock Alert
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="warning.main">
                              {statistics.lowStock}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                            <Warning />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Out of Stock
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="error.main">
                              {statistics.outOfStock}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                            <Cancel />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                </>
              )}
              
            </Grid>

            {/* Search and Filters Bar */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by name, SKU, category, or requester..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <GlowingButton
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={handleFilterClick}
                    >
                      Filters
                    </GlowingButton>
                    
                    {/* Only show Add Supply button on Supplies tab */}
                    {activeTab === 1 && (
                      <GlowingButton
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenAddDialog}
                      >
                        Add Supply
                      </GlowingButton>
                    )}
                    
                    <GlowingButton
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleExportToExcel}
                    >
                      Export
                    </GlowingButton>
                    
                    <IconButton onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setSelectedStatus('all');
                      setDateRange({ start: '', end: '' });
                      if (activeTab === 1) fetchSupplies();
                    }}>
                      <Refresh />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
            
            {/* Active Filters Display */}
            {(selectedCategory !== 'all' || selectedStatus !== 'all' || dateRange.start) && (
              <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Active Filters:</Typography>
                {selectedCategory !== 'all' && (
                  <Chip label={`Category: ${selectedCategory}`} onDelete={() => setSelectedCategory('all')} size="small" />
                )}
                {selectedStatus !== 'all' && (
                  <Chip label={`Status: ${selectedStatus}`} onDelete={() => setSelectedStatus('all')} size="small" />
                )}
                {dateRange.start && (
                  <Chip label={`From: ${dateRange.start}`} onDelete={() => setDateRange({ ...dateRange, start: '' })} size="small" />
                )}
                {dateRange.end && (
                  <Chip label={`To: ${dateRange.end}`} onDelete={() => setDateRange({ ...dateRange, end: '' })} size="small" />
                )}
                <Button size="small" onClick={clearFilters} sx={{ ml: 1 }}>Clear All</Button>
              </Box>
            )}

            {/* Table */}
            {renderCurrentTable()}
          </Paper>
        </Box>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, width: 320, borderRadius: '16px' } }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Filters</Typography>
        
        {(activeTab === 0 || activeTab === 1) && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Category">
              <MenuItem value="all">All Categories</MenuItem>
              {activeTab === 0 
                ? getInventoryCategories().filter(c => c !== 'all').map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))
                : categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))
              }
            </Select>
          </FormControl>
        )}
        
        {(activeTab === 0) && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="In Stock">In Stock</MenuItem>
              <MenuItem value="Low Stock">Low Stock</MenuItem>
              <MenuItem value="Out of Stock">Out of Stock</MenuItem>
            </Select>
          </FormControl>
        )}
        
        {(activeTab === 1 || activeTab === 2) && (
          <>
            <TextField
              fullWidth size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }}
              value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }}
              value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              sx={{ mb: 2 }}
            />
          </>
        )}
        
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={clearFilters} size="small">Clear All</Button>
          <Button variant="contained" onClick={handleFilterClose} size="small">Apply</Button>
        </Stack>
      </Popover>

      {/* Add Dialog for Supplies */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>Add New Supply</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={itemsList}
                getOptionLabel={(option) => option.item_name}
                onChange={handleItemSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Select Item Name"
                    placeholder="Search or select an item..."
                    helperText="Select an item to auto-fill category and unit"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>{option.item_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Category: {option.category} | Unit: {option.unit_of_measurement}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Category"
                value={formData.category}
                disabled
                helperText="Auto-filled from selected item"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Unit of Measurement"
                value={formData.unit_of_measurement}
                disabled
                helperText="Auto-filled from selected item"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Quantity Received"
                value={formData.quantity_received}
                onChange={(e) => setFormData({ ...formData, quantity_received: e.target.value })}
                InputProps={{ inputProps: { min: 0, step: 0.001 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Total Invoice Value (₦)"
                value={formData.total_invoice_value}
                onChange={(e) => setFormData({ ...formData, total_invoice_value: e.target.value })}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date (Optional)"
                InputLabelProps={{ shrink: true }}
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                label="Checked By"
                value={formData.checked_by}
                onChange={(e) => setFormData({ ...formData, checked_by: e.target.value })}
              
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSupply} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Add Supply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog for Supplies */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Supply</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Item Name"
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Unit of Measurement</InputLabel>
                <Select
                  value={formData.unit_of_measurement}
                  onChange={(e) => setFormData({ ...formData, unit_of_measurement: e.target.value })}
                  label="Unit of Measurement"
                >
                  {unitOptions.map(unit => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Quantity Received"
                value={formData.quantity_received}
                onChange={(e) => setFormData({ ...formData, quantity_received: e.target.value })}
                InputProps={{ inputProps: { min: 0, step: 0.001 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Total Invoice Value (₦)"
                value={formData.total_invoice_value}
                onChange={(e) => setFormData({ ...formData, total_invoice_value: e.target.value })}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                disabled
                label="Checked By"
                value={formData.checked_by}
                onChange={(e) => setFormData({ ...formData, checked_by: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                InputLabelProps={{ shrink: true }}
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSupply} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedSupply?.item_name}"? This action cannot be undone.
          </Typography>
          {selectedSupply?.approval_status === 1 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This supply is approved and cannot be deleted. Please request deletion approval.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSupply}
            disabled={loading || selectedSupply?.approval_status === 1}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryManagement;